import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'

function read(key) { try { return key ? JSON.parse(sessionStorage.getItem(key) || 'null') : null } catch { return null } }
/** No booking/payment retry here: reconciliation may cancel an unpaid checkout and returns server evidence. */
export function useMemberBookingRecovery({ clubCode, module, memberId, post, onCompleted, onReleased }) {
  const { user } = useAuth0()
  const identity = user?.sub || memberId
  const storageKey = identity ? `members-booking-operation:${clubCode}:${identity}:${module}` : null
  const keyRef = useRef(read(storageKey)?.key || null)
  const [pending, setPending] = useState(Boolean(keyRef.current))
  const [checking, setChecking] = useState(false)
  const [status, setStatus] = useState(null)
  const popup = useRef(null), busy = useRef(false), active = useRef(true), scope = useRef(storageKey)
  const callbacks = useRef({ onCompleted, onReleased }); callbacks.current = { onCompleted, onReleased }
  useEffect(() => {
    active.current = true; scope.current = storageKey; popup.current = null; busy.current = false; setChecking(false)
    keyRef.current = read(storageKey)?.key || null
    setPending(Boolean(keyRef.current)); setStatus(null)
    return () => { active.current = false }
  }, [storageKey])
  const requestKey = useCallback(() => keyRef.current ||= crypto.randomUUID(), [])
  const markPending = useCallback(() => {
    const key = requestKey(); setPending(true)
    try { if (storageKey) sessionStorage.setItem(storageKey, JSON.stringify({ key })) } catch {}
  }, [requestKey, storageKey])
  const release = useCallback(() => {
    keyRef.current = null; popup.current = null; setPending(false); setStatus(null)
    try { if (storageKey) sessionStorage.removeItem(storageKey) } catch {}
  }, [storageKey])
  const reconcile = useCallback(async () => {
    if (!keyRef.current || busy.current) return
    busy.current = true; setChecking(true)
    const requestedScope = storageKey
    try {
      const response = await post(`/api/members-portal/${clubCode}/booking-operations/${keyRef.current}/reconcile`, { module })
      const result = await response.json()
      if (!active.current || scope.current !== requestedScope) return
      if (response.ok && result.status === 'completed' && result.booking_id) {
        release(); window.dispatchEvent(new CustomEvent('members:upcoming-bookings-invalidated'))
        callbacks.current.onCompleted?.(result.booking_id)
      } else if (response.ok && result.status === 'cancelled' && result.safe_to_retry === true) {
        try { popup.current?.close?.() } catch {}
        release(); callbacks.current.onReleased?.()
      } else setStatus(result.status === 'needs_help' ? 'needs_help' : 'pending')
    } catch { if (active.current && scope.current === requestedScope) setStatus('unknown') }
    finally { if (active.current && scope.current === requestedScope) { busy.current = false; setChecking(false) } }
  }, [storageKey,clubCode,module,post,release])
  useEffect(() => {
    if (!pending) return undefined
    const checkClosed = () => { if (popup.current?.closed) { popup.current = null; reconcile() } }
    const timer = window.setInterval(checkClosed, 1000)
    window.addEventListener('focus', checkClosed)
    return () => { window.clearInterval(timer); window.removeEventListener('focus', checkClosed) }
  }, [pending,reconcile])
  return { pending, checking, status, requestKey, markPending, release, reconcile, watchCheckout: value => { popup.current = value } }
}
